import type { Session } from "@supabase/supabase-js";

/** Синхрон с `useAuth`: пока true — считаем сценарий сброса пароля (в т.ч. после редиректа с `/auth/callback` без hash). */
const STORAGE_RECOVERY_PENDING = "lb-auth-recovery-pending";

export function readRecoveryPendingFlag(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return sessionStorage.getItem(STORAGE_RECOVERY_PENDING) === "1";
  } catch {
    return false;
  }
}

export function setRecoveryPendingFlag(on: boolean): void {
  if (typeof window === "undefined") return;
  try {
    if (on) sessionStorage.setItem(STORAGE_RECOVERY_PENDING, "1");
    else sessionStorage.removeItem(STORAGE_RECOVERY_PENDING);
  } catch {
    /* ignore */
  }
}

/**
 * Расшифровка payload JWT (без проверки подписи — только для клиентских подсказок UI;
 * сервер по-прежнему должен валидировать токен).
 */
function decodeJwtPayload(accessToken: string): Record<string, unknown> | null {
  try {
    const parts = accessToken.split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const json = atob(padded);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function amrHasRecovery(amr: unknown): boolean {
  if (!Array.isArray(amr)) return false;
  return amr.some((entry) => {
    if (entry === "recovery") return true;
    if (entry && typeof entry === "object" && "method" in entry) {
      return (entry as { method: string }).method === "recovery";
    }
    return false;
  });
}

/**
 * Сессия после перехода по ссылке сброса пароля: в JWT в `amr` указан метод `recovery`
 * (см. https://supabase.com/docs/guides/auth/jwt-fields ).
 * После успешного `updateUser({ password })` выдаётся обычный токен без `recovery`.
 */
export function isRecoveryJwtSession(session: Session | null): boolean {
  if (!session?.access_token) return false;
  const payload = decodeJwtPayload(session.access_token);
  if (!payload) return false;
  return amrHasRecovery(payload.amr);
}

/**
 * В URL явно сценарий **сброса пароля** (recovery), а не любое письмо с токеном.
 *
 * Раньше считалось «recovery» при любом `access_token` в hash на `/auth/callback` — из‑за этого
 * подтверждение регистрации (`type=signup`) помечалось как recovery, и после колбэка
 * `ProtectedRoute` уводил на `/auth/reset-password`.
 */
export function isRecoveryUrlPresent(): boolean {
  if (typeof window === "undefined") return false;
  const p = window.location.pathname.replace(/\/+$/, "") || "/";
  if (!["/auth/reset-password", "/auth/callback", "/"].includes(p)) return false;
  const h = window.location.hash || "";
  const q = new URLSearchParams(window.location.search);
  if (/type=recovery/.test(h) || q.get("type") === "recovery") return true;
  if (p === "/auth/reset-password" && (/access_token|refresh_token/.test(h) || q.has("code"))) return true;
  return false;
}
